import { inject, signal, Signal, WritableSignal } from '@angular/core';
import { SecureStorageService } from '../../core/services/secure-storage.service';

export abstract class BaseFilterService<T extends Record<string, any>> {
  protected abstract readonly storageKey: string;
  protected abstract readonly emptyFilters: T;
  protected secureStorage = inject(SecureStorageService);

  readonly filters: WritableSignal<T>;

  constructor() {
    // Defer init — subclass fields aren't assigned until after super() returns.
    // Use a placeholder signal, then immediately overwrite in subclass via init().
    this.filters = signal<T>({} as T);
  }

  /** Must be called in subclass constructor after super() */
  protected init(): void {
    this.filters.set(this.loadFromStorage());
  }

  applyFilters(filters: T): void {
    this.filters.set({ ...filters });
    this.saveToStorage(filters);
  }

  resetFilters(): void {
    this.filters.set({ ...this.emptyFilters });
    this.removeFromStorage();
  }

  /** Override in subclass to build API payload */
  abstract buildPayload(filters?: T): Record<string, any>;

  protected abstract parseStored(parsed: any): T;

  private saveToStorage(filters: T): void {
    try {
      this.secureStorage.setLocalObject(this.storageKey, filters);
    } catch { /* storage full or unavailable */ }
  }

  private removeFromStorage(): void {
    try {
      this.secureStorage.removeLocal(this.storageKey);
    } catch { /* ignore */ }
  }

  private loadFromStorage(): T {
    try {
      const parsed = this.secureStorage.getLocalObject<T>(this.storageKey);
      if (parsed) {
        return this.parseStored(parsed);
      }
    } catch { /* corrupted data */ }
    return { ...this.emptyFilters };
  }
}
